import { z } from 'zod';
import { DocId, IsoTimestamp, TenantId } from '../common.js';
import { sourceDefaultClassificationFields } from '../classification.js';
/**
 * Code Sources — repos ingested through the unified `mcp.aiagents.1nebula.com`
 * gateway (JSON-RPC over `/mcp`). The gateway brokers per-employee OAuth to
 * the upstream provider and exposes a Code-Mode tool surface for token-cheap
 * crawling.
 *
 * Provider matrix (verified 2026-05-11 against AGENT_INTEGRATION.md):
 *  - `github`         — Code Mode ON via `/mcp`. Recommended.
 *  - `bitbucket`      — Plain REST proxy at `/api/bitbucket/*` (NOT Code
 *                       Mode). Same Entra bearer as `/mcp`; the gateway
 *                       substitutes the employee's stored Bitbucket OAuth
 *                       token and forwards to `api.bitbucket.org/2.0`.
 *  - `atlassian_rovo` — Code Mode ON, Jira + Confluence consumption flows.
 *                       NOT used for code sources (Rovo's Bitbucket tools
 *                       are stub-only). The sync path throws if selected.
 *  - `azure_devops`   — Plain REST proxy at `/api/azure_devops/{org}/*`
 *                       (NOT Code Mode). Same Entra bearer as `/mcp`; the
 *                       gateway substitutes the employee's stored ADO
 *                       token. `repoRef` is `{project}/{repo}` — the org
 *                       slug is hard-coded into the gateway URL prefix.
 *
 * One row = one repo + branch. For github + bitbucket, `repoRef` is the
 * `workspace/repo` (or `owner/repo`) slug; for azure_devops it's
 * `project/repo`. The connector type lives in `provider`, no longer
 * inferred from the slug shape.
 *
 * Tenant-scoped: `tenants/{tenantId}/code_sources/{id}`.
 */
export const CodeProvider = z.enum(['github', 'atlassian_rovo', 'bitbucket', 'azure_devops']);
export const CodeSourceStatus = z.enum(['idle', 'running', 'succeeded', 'failed']);
export const CodeSourcePhase = z.enum(['listing', 'fetching', 'ingesting', 'completed']);
export const CodeSyncFileStatus = z.enum(['in_progress', 'succeeded', 'failed', 'skipped']);
export const CodeSyncFileEntry = z.object({
    path: z.string().min(1).max(400),
    status: CodeSyncFileStatus,
    ts: IsoTimestamp,
    durationMs: z.number().int().nonnegative().optional(),
    errorMessage: z.string().max(300).optional(),
    chunkCount: z.number().int().nonnegative().optional(),
});
export const CodeSyncProgress = z.object({
    startedAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
    phase: CodeSourcePhase,
    totalFiles: z.number().int().nonnegative(),
    currentIndex: z.number().int().nonnegative(),
    currentPath: z.string().max(400).optional(),
    filesSucceeded: z.number().int().nonnegative(),
    filesFailed: z.number().int().nonnegative(),
    filesSkipped: z.number().int().nonnegative(),
    recentFiles: z.array(CodeSyncFileEntry).max(50).default([]).optional(),
    /**
     * True when the underlying listTree call hit its call/entry cap and the
     * tree returned is incomplete. Admin UI should surface a warning badge so
     * the operator knows the indexed view of the repo is partial. Set during
     * the `listing` phase and persists through `completed`. Bitbucket-only
     * today (the BFS that descends `/repositories/.../src/` cap).
     */
    treeTruncated: z.boolean().optional(),
});
export const CodeSource = z.object({
    id: DocId,
    tenantId: TenantId,
    provider: CodeProvider,
    /**
     * Gateway-defined repo identifier. The gateway resolves provider +
     * workspace/owner/org + repo from this opaque slug; we do not parse it.
     */
    repoRef: z.string().min(1).max(200),
    /** Branch to crawl. Default 'main'; older repos may use 'master'. */
    branch: z.string().min(1).max(100).default('main'),
    ring: z.enum(['ring0', 'ring1']).default('ring1'),
    /** Glob patterns relative to repo root. Empty = use defaults. */
    includeGlobs: z.array(z.string().min(1).max(200)).max(50).default([]),
    excludeGlobs: z.array(z.string().min(1).max(200)).max(50).default([]),
    /** Operator-supplied display name. Falls back to repoRef in the UI. */
    label: z.string().min(1).max(200).optional(),
    status: CodeSourceStatus.default('idle'),
    errorCode: z.string().max(80).optional(),
    errorMessage: z.string().max(2000).optional(),
    lastSyncedAt: IsoTimestamp.optional(),
    lastCommitSha: z.string().min(1).max(64).optional(),
    pagesIngested: z.number().int().min(0).default(0),
    /**
     * Live progress while a sync is in flight. Cleared/replaced on each new
     * sync. Frontend polls and renders a progress strip when present +
     * status === 'running'. On terminal sync the final recentFiles snapshot
     * is preserved so operators can review the run post-hoc.
     */
    syncProgress: CodeSyncProgress.optional(),
    createdAt: IsoTimestamp,
    createdBy: z.string().min(1).max(200),
    updatedAt: IsoTimestamp,
    /**
     * Entra `oid` of the operator who registered this source. The daily
     * Cloud Scheduler crawl impersonates this user when calling the gateway,
     * because the gateway maps `oid → upstream refresh token` per employee.
     * If the operator leaves the org the crawl will go dark (gateway returns
     * `403 No active authorisation`) and the admin must re-register the
     * source under a current employee's identity.
     */
    ownerOid: z.string().min(1).max(200),
    /** Email of the registering operator. Audit-log readability only. */
    ownerEmail: z.string().max(320),
    /**
     * ISO 27001 classification defaults that cascade to every code chunk
     * ingested from this source. Defaults to internal / [] — code is
     * org-internal but not contract-confidential by default.
     */
    ...sourceDefaultClassificationFields,
    /**
     * Specialisations attached at the source level — applied to every code
     * chunk ingested from this repo. Names must exist in the
     * `specialisations/` Firestore collection (validated at write time by
     * the admin route). Edited only via /admin/specialisations — not
     * surfaced on the Code sources page itself.
     */
    specialisations: z.array(z.string().min(1).max(60)).max(10).default([]).optional(),
});
/**
 * Default include globs covering the core stacks we ingest from. Operators
 * can override per-source via the admin UI. Order is informational; the
 * gateway-side matcher treats the list as a set.
 */
export const CODE_SOURCE_DEFAULT_INCLUDE_GLOBS = [
    '**/*.cs',
    '**/*.csx',
    '**/*.razor',
    '**/*.cshtml',
    '**/*.csproj',
    '**/*.sln',
    '**/*.ts',
    '**/*.tsx',
    '**/*.html',
    '**/*.scss',
    '**/*.css',
    '**/*.sql',
    '**/*.yml',
    '**/*.yaml',
    '**/*.json',
    '**/*.xml',
    '**/*.config',
    '**/Dockerfile',
    '**/azure-pipelines.yml',
    '**/.gitlab-ci.yml',
];
/**
 * Default exclude globs — generated artifacts, vendored dependencies,
 * build output, designer-emitted code. These are noise for retrieval.
 */
export const CODE_SOURCE_DEFAULT_EXCLUDE_GLOBS = [
    'bin/**',
    'obj/**',
    'packages/**',
    '.vs/**',
    'node_modules/**',
    'dist/**',
    '.angular/**',
    '**/*.Designer.cs',
    '**/*.g.cs',
    '**/*.generated.cs',
    '**/AssemblyInfo.cs',
    '**/*.min.js',
    '**/*.min.css',
    '**/.git/**',
];
//# sourceMappingURL=code-source.js.map